/**
 * Tipi ed etichette condivisi dalle tre pagine del Piano di adozione.
 *
 * Stanno qui e non in ciascuna pagina perché le stesse etichette compaiono nel
 * piano, in "Le mie attività" e nel monitoraggio: tenerle in tre posti diversi
 * garantirebbe che prima o poi divergano.
 */

export type StatoPiano = 'BOZZA' | 'ATTIVO' | 'CONCLUSO';
export type StatoAttivita = 'DA_FARE' | 'IN_CORSO' | 'COMPLETATA' | 'NON_APPLICABILE';
export type TipoFeedback = 'OPINIONE' | 'DIFFICOLTA' | 'PROPOSTA' | 'SEGNALAZIONE';
export type StatoFeedback = 'APERTO' | 'LETTO' | 'CHIUSO';

export interface UtenteRif { id: number; nome: string; email: string }
export interface SocietaRif { id: number; nome: string }

export interface Avanzamento {
  totale: number;
  completate: number;
  nonApplicabili: number;
  inCorso: number;
  percentuale: number;
  stato: 'DA_INIZIARE' | 'IN_CORSO' | 'COMPLETATO';
  inRitardo: boolean;
}

export interface Attivita {
  id: number;
  titolo: string;
  descrizione: string | null;
  assegnatoAId: number;
  assegnatoA: UtenteRif;
  scadenza: string | null;
  stato: StatoAttivita;
  notaAssegnatario: string | null;
  completatoAt: string | null;
  inRitardo: boolean;
}

export interface Obiettivo {
  id: number;
  titolo: string;
  descrizione: string | null;
  scadenza: string | null;
  ordine: number;
  attivo: boolean;
  attivita: Attivita[];
  avanzamento: Avanzamento;
}

export interface Feedback {
  id: number;
  pianoId: number;
  obiettivoId: number | null;
  attivitaId: number | null;
  autoreId: number;
  autore: UtenteRif;
  tipo: TipoFeedback;
  testo: string;
  stato: StatoFeedback;
  rispostaTesto: string | null;
  rispostaDa: UtenteRif | null;
  rispostaAt: string | null;
  createdAt: string;
  obiettivo: { id: number; titolo: string } | null;
  attivita: { id: number; titolo: string } | null;
}

export interface Piano {
  id: number;
  societaId: number;
  societa: SocietaRif;
  titolo: string;
  descrizione: string | null;
  dataInizio: string;
  dataFine: string;
  stato: StatoPiano;
  giorniPreavviso: number;
  createdBy: UtenteRif;
  _count?: { obiettivi: number };
}

export interface PianoDettaglio extends Piano {
  obiettivi: Obiettivo[];
  feedback: Feedback[];
  riepilogo: Avanzamento & {
    obiettivi: number;
    obiettiviInRitardo: number;
    feedbackAperti: number;
  };
}

export interface MiaAttivita {
  id: number;
  titolo: string;
  descrizione: string | null;
  scadenza: string | null;
  stato: StatoAttivita;
  notaAssegnatario: string | null;
  completatoAt: string | null;
  inRitardo: boolean;
  obiettivo: { id: number; titolo: string };
  piano: { id: number; titolo: string; societaId: number; dataFine: string; societa: SocietaRif };
}

export interface RigaMonitoraggio {
  societaId: number;
  societaNome: string;
  piano: { id: number; titolo: string; dataInizio: string; dataFine: string } | null;
  totale: number;
  chiuse: number;
  percentuale: number;
  obiettiviInRitardo: number;
  attivitaInRitardo: number;
  feedbackAperti: number;
  ultimoMovimento: string | null;
}

// ── Etichette ────────────────────────────────────────────────────────────────

export const STATO_PIANO_LABEL: Record<StatoPiano, { label: string; variant: string }> = {
  BOZZA:    { label: 'Bozza',    variant: 'info' },
  ATTIVO:   { label: 'Attivo',   variant: 'success' },
  CONCLUSO: { label: 'Concluso', variant: 'neutral' },
};

export const STATO_ATTIVITA_LABEL: Record<StatoAttivita, { label: string; variant: string }> = {
  DA_FARE:         { label: 'Da fare',         variant: 'neutral' },
  IN_CORSO:        { label: 'In corso',        variant: 'info' },
  COMPLETATA:      { label: 'Completata',      variant: 'success' },
  // Non è un successo: chiude la riga, ma il monitoraggio la conta a parte.
  NON_APPLICABILE: { label: 'Non applicabile', variant: 'warning' },
};

export const TIPO_FEEDBACK_LABEL: Record<TipoFeedback, { label: string; variant: string }> = {
  OPINIONE:     { label: 'Opinione',     variant: 'info' },
  DIFFICOLTA:   { label: 'Difficoltà',   variant: 'warning' },
  PROPOSTA:     { label: 'Proposta',     variant: 'primary' },
  SEGNALAZIONE: { label: 'Segnalazione', variant: 'danger' },
};

// ── Formattazione ────────────────────────────────────────────────────────────

export function fmtData(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('it-IT');
}

export function fmtDataOra(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
}

export function oggiISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const gg = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${gg}`;
}

/**
 * Classi della barra di avanzamento. La larghezza è una classe a scatti di 5%
 * perché il design system vieta gli stili inline.
 */
export function classeProgress(a: { percentuale: number; inRitardo: boolean; stato: string }): string {
  const passo = Math.round(a.percentuale / 5) * 5;
  const colore = a.stato === 'COMPLETATO'
    ? ' bop-adozione__progress-bar--completo'
    : (a.inRitardo ? ' bop-adozione__progress-bar--ritardo' : '');
  return `bop-adozione__progress-bar--p${passo}${colore}`;
}

/**
 * Giorni interi che mancano a una scadenza, contati a giorno e non a istante:
 * "scade oggi" non deve dipendere dall'ora in cui si apre la pagina.
 */
export function giorniAllaScadenza(iso: string | null, riferimento = new Date()): number | null {
  if (!iso) return null;
  const s = new Date(iso);
  if (Number.isNaN(s.getTime())) return null;
  const a = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const b = new Date(riferimento.getFullYear(), riferimento.getMonth(), riferimento.getDate());
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

export type GruppoScadenza = 'RITARDO' | 'SETTIMANA' | 'AVANTI' | 'CHIUSE';

export const GRUPPO_LABEL: Record<GruppoScadenza, string> = {
  RITARDO:   'In ritardo',
  SETTIMANA: 'Questa settimana',
  AVANTI:    'Più avanti',
  CHIUSE:    'Completate',
};

/** In quale blocco di "Le mie attività" finisce una riga. */
export function gruppoDi(a: MiaAttivita, riferimento = new Date()): GruppoScadenza {
  if (a.stato === 'COMPLETATA' || a.stato === 'NON_APPLICABILE') return 'CHIUSE';
  const giorni = giorniAllaScadenza(a.scadenza, riferimento);
  if (giorni === null) return 'AVANTI';
  if (giorni < 0) return 'RITARDO';
  if (giorni <= 7) return 'SETTIMANA';
  return 'AVANTI';
}

export const ORDINE_GRUPPI: GruppoScadenza[] = ['RITARDO', 'SETTIMANA', 'AVANTI', 'CHIUSE'];
